import { useEffect, useState } from "react";
import { MdDescription, MdInsertDriveFile, MdInventory } from "react-icons/md";
import ReactMarkdown from "react-markdown";
import { summaryStorage } from "../../../services/summaryStorage";
import ExportButton from "../../common/ExportButton";
import ExportModal from "../../common/ExportModal";
import styles from "./HistoryTab.module.css";

const HistoryTab = ({ isActive, onImport }) => {
  const [summaries, setSummaries] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [exportModalData, setExportModalData] = useState(null);

  useEffect(() => {
    const loadSummaries = async () => {
      const storedSummaries = await summaryStorage.getSummaries();
      setSummaries(storedSummaries);
    };

    if (isActive) {
      loadSummaries();
    }
  }, [isActive]);

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this summary?")) {
      const success = await summaryStorage.deleteSummary(id);
      if (success) {
        setSummaries((prevSummaries) => prevSummaries.filter((s) => s.id !== id));
      }
    }
  };

  const handleClearAll = async () => {
    if (
      window.confirm(
        "Are you sure you want to clear all History? This action cannot be undone."
      )
    ) {
      const success = await summaryStorage.clearAll();
      if (success) {
        setSummaries([]);
      }
    }
  };

  const getExportDataForSummary = (summary) => {
    return {
      summary: summary.summary,
      filename: `summary-${summary.id}`,
      metadata: summary.metadata,
      originalText: summary.originalText,
      method: summary.method,
      model: summary.model,
      temperature: summary.temperature,
      topP: summary.topP,
      intendedAudience: summary.intendedAudience,
      summaryStyle: summary.summaryStyle,
      fileName: summary.fileName,
      fileSize: summary.fileSize,
      fileType: summary.fileType
    };
  };

  const toggleExpanded = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const formatDate = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return "";

    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const truncateText = (text, maxLength = 150) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "Unknown size";
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / 1048576).toFixed(1) + " MB";
  };

  if (summaries.length === 0) {
    return (
      <div className={styles.historyTab}>
        <div className={styles.header}>
          <h2>History</h2>
          <p className={styles.emptyMessage}>
            No summaries saved.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.historyTab}>
      <div className={styles.header}>
        <h2>History</h2>
        <div className={styles.headerActions}>
          <span className={styles.count}>{summaries.length} summaries</span>
          <button onClick={handleClearAll} className={styles.clearAllBtn}>
            Clear All
          </button>
        </div>
      </div>

      <div className={styles.summariesList}>
        {summaries.map((summary) => (
          <div key={summary.id} className={styles.summaryCard}>
            <div className={styles.summaryHeader}>
              <div className={styles.summaryMeta}>
                <h3 className={styles.summaryTitle}>
                  {summary.metadata?.custom_title || summary.fileName || "Untitled Summary"}
                </h3>
                <span className={styles.timestamp}>
                  {formatDate(summary.timestamp || summary.created_at)}
                </span>
              </div>
              <div className={styles.summaryActions}>
                <button
                  onClick={() => toggleExpanded(summary.id)}
                  className={styles.expandBtn}
                >
                  {expandedId === summary.id ? "Collapse" : "Expand"}
                </button>
                <ExportButton
                  onClick={() => setExportModalData(getExportDataForSummary(summary))}
                  buttonText="Export"
                  showIcon={false}
                />
                {onImport && (
                  <button
                    onClick={() => onImport(summary)}
                    className={styles.importBtn}
                    title="Import to playground for editing"
                  >
                    Load
                  </button>
                )}
                <button
                  onClick={() => handleDelete(summary.id)}
                  className={styles.deleteBtn}
                >
                  Delete
                </button>
              </div>
            </div>

            <div className={styles.summaryPreview}>
              {expandedId === summary.id ? (
                <div className={styles.fullContent}>
                  {summary.fileName ? (
                    <div className={styles.fileInfo}>
                      <h4>Source File:</h4>
                      <div className={styles.fileDetails}>
                        <p><strong><MdInsertDriveFile /> Name:</strong> {summary.fileName}</p>
                        {summary.fileSize && <p><strong><MdInventory /> Size:</strong> {formatFileSize(summary.fileSize)}</p>}
                        {summary.fileType && <p><strong><MdDescription /> Type:</strong> {summary.fileType}</p>}
                      </div>
                    </div>
                  ) : null}
                  <div className={styles.summaryResult}>
                    <ReactMarkdown>{summary.summary}</ReactMarkdown>
                  </div>
                </div>
              ) : (
                <p className={styles.previewText}>
                  {truncateText(summary.summary)}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>

      <ExportModal
        isOpen={exportModalData !== null}
        onClose={() => setExportModalData(null)}
        exportData={exportModalData || {}}
      />
    </div>
  );
};

export default HistoryTab;
